import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
} from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'

import { useToast } from '@/components/ui/use-toast'
import { createTool, deleteTool, listTools, updateTool } from '@/lib/api/client'
import CustomError from '@/lib/api/CustomError'
import queryClient from '@/queryClient'
import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'

export type Tool = {
  id: string
  name: string
}

export type ListToolsPage = {
  tools: Tool[]
  totalTools: number
  limit: number
  offset: number
  search: string
  documentVersionId?: string
}

export const flattenToolPages = (
  data?: InfiniteData<ListToolsPage>,
): Tool[] => {
  if (!data?.pages?.length) return []

  const toolsById = new Map<string, Tool>()
  data.pages.forEach((page) => {
    page.tools.forEach((tool) => {
      if (!toolsById.has(tool.id)) {
        toolsById.set(tool.id, tool)
      }
    })
  })

  return Array.from(toolsById.values())
}

const invalidateToolsQueryCache = async () => {
  /**
   * Invariant: every tools mutation must fan out a refresh to all TOOLS
   * list variants (search/pagination combinations).
   */
  await queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.TOOLS],
    exact: false,
  })
}

export const useListToolsQuery = (
  {
    limit = 100,
    offset = 0,
    search = '',
    documentVersionId,
    enabled = true,
  }: {
    limit?: number
    offset?: number
    search?: string
    documentVersionId?: string
    enabled?: boolean
  } = {
    limit: 100,
    offset: 0,
    search: '',
    documentVersionId: undefined,
    enabled: true,
  },
) => {
  const { toast } = useToast()

  return useInfiniteQuery<ListToolsPage>({
    enabled,
    refetchOnMount: true,
    queryKey: [QUERY_KEYS.TOOLS, { limit, offset, search, documentVersionId }],
    queryFn: async ({ pageParam }) => {
      try {
        const nextPageParam = (pageParam || {
          limit,
          offset,
          search,
          documentVersionId,
        }) as {
          limit?: number
          offset?: number
          search?: string
          documentVersionId?: string
        }
        const pageLimit = nextPageParam.limit ?? limit
        const pageOffset = nextPageParam.offset ?? offset
        const pageSearch = nextPageParam.search ?? search
        const pageDocumentVersionId =
          nextPageParam.documentVersionId ?? documentVersionId

        const resp = await listTools({
          limit: pageLimit,
          offset: pageOffset,
          search: pageSearch,
          documentVersionId: pageDocumentVersionId,
        })
        const tools = (resp.tools || []).reduce((accum, tool) => {
          if (
            typeof tool?.id !== 'string' ||
            tool.id.trim() === '' ||
            typeof tool?.name !== 'string' ||
            tool.name.trim() === ''
          ) {
            return accum
          }

          accum.push({
            id: tool.id,
            name: tool.name,
          })

          return accum
        }, [] as Tool[])

        return {
          tools,
          totalTools: resp.totalTools || 0,
          limit: pageLimit,
          offset: pageOffset,
          search: pageSearch,
          documentVersionId: pageDocumentVersionId,
        }
      } catch (error) {
        if (error instanceof CustomError) {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        }
        throw error
      }
    },
    getNextPageParam: (lastPage) => {
      const pageSize = lastPage.limit
      const returned = (lastPage.tools || []).length
      const loadedThrough = lastPage.offset + returned
      const hasMore =
        pageSize > 0 &&
        returned > 0 &&
        loadedThrough < (lastPage.totalTools || 0)
      if (!hasMore) return undefined
      return {
        limit: pageSize,
        offset: lastPage.offset + pageSize,
        search: lastPage.search,
        documentVersionId: lastPage.documentVersionId,
      }
    },
    initialPageParam: { limit, offset, search, documentVersionId },
    placeholderData: keepPreviousData,
  })
}

export const useCreateTool = () => {
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_TOOL],
    mutationFn: async ({ name }: { name: string }) => {
      const tool = await createTool({ name })
      if (!tool.id) {
        throw new Error('Missing tool ID')
      }
      return tool
    },
    onSuccess: async () => {
      await invalidateToolsQueryCache()
    },
    onError: (error) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useUpdateTool = () => {
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_TOOL],
    mutationFn: async ({ toolId, name }: { toolId: string; name: string }) => {
      const tool = await updateTool(toolId, { name })
      return { ...tool, id: toolId }
    },
    onSuccess: async () => {
      await invalidateToolsQueryCache()
    },
    onError: (error) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}

export const useDeleteTool = () => {
  const { toast } = useToast()
  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_TOOL],
    mutationFn: async ({ toolId }: { toolId: string }) => {
      await deleteTool(toolId)
      return {
        toolId,
      }
    },
    onSuccess: async () => {
      await invalidateToolsQueryCache()
    },
    onError: (error) => {
      if (error instanceof CustomError) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
      }
    },
  })
}
